import { handle } from 'redux-pack';
import {
	SET_EVENT,
	LOAD_MEETINGS_EVENTS_LEGACY_PRODUCER,
	LOAD_MEETINGS_SHOWROOMS_PRODUCER,
	LOAD_MEETINGS_PRESENTER,
	SET_LIVE_CHANNELS,
	UPDATE_EVENTS_PRESENTER,
	LOAD_MEETINGS_STANDALONE_GREENROOM_PRODUCER,
	LOAD_MEETINGS_EVENTS_PRODUCER,
	GET_DEFAULT_BACKGROUNDS,
	LOAD_VBGS,
	SELECT_VBG,
	ADD_UPLOADING_VBG,
	REMOVE_UPLOADING_VBG,
} from '../actions/content';
import { UserMedia } from './media';
import { STATIC_ASSET_URL } from '../../utils/content-templates';
import { VirtualBg, Blur } from '../types/content';
import { decodeHtmlEntities } from '../../utils/utils';

export interface MediaBackground {
	image: string;
	thumb: string;
}

const urlPrepend = (name: string) => `${STATIC_ASSET_URL}/virtual_backgrounds/${name}`;
// first 3 are jpg, the rest are png.
const fileType = (index: number) => (index < 3 ? 'jpg' : 'png');

const numberOfDefaultBgs = 44;

const defaultBgs: Array<VirtualBg> = Array.from({ length: numberOfDefaultBgs }, (_, i) => ({
	url: urlPrepend(`full/${i + 1}.${fileType(i)}`),
	thumbnail: urlPrepend(`thumbs/${i + 1}.jpeg`),
	originalType: fileType(i),
	blur: 0,
	uniqueId: `default_${i + 1}`,
}));

export interface ContentState {
	event: any;
	isLoadingEvent: boolean;
	eventError: boolean;
	loadedEvent: any;
	events: any[];
	loadingEvents: boolean;
	eventsError: string | null;
	meetings: any[];
	loadingMeetings: boolean;
	meetingsError: string | null;
	liveChannels: number[];
	defaultBackgrounds: MediaBackground[];
	virtualBackgrounds: VirtualBg[];
	defaultVBGS: VirtualBg[];
	loadingVbgs: boolean;
	recentVbgUpload: boolean;
	selectedVBGUrl: string | null;
	selectedVBG: VirtualBg | null;
	uploadingVbgs: Map<string, boolean>;
}

export const initialState: ContentState = {
	event: {},
	isLoadingEvent: false,
	eventError: false,
	loadedEvent: {},
	events: [],
	loadingEvents: true,
	eventsError: null,
	meetings: [],
	loadingMeetings: false,
	meetingsError: null,
	liveChannels: [],
	defaultBackgrounds: [],
	virtualBackgrounds: [],
	defaultVBGS: defaultBgs,
	loadingVbgs: true,
	recentVbgUpload: false,
	selectedVBGUrl: null,
	selectedVBG: null,
	uploadingVbgs: new Map(),
};

interface Action {
	meta: any;
	type: string;
	payload: any;
}

const actions: Map<any, Function> = new Map([
	[SET_EVENT, (state: ContentState, action: Action) => ({ ...state, event: action.payload })],
	[LOAD_MEETINGS_EVENTS_PRODUCER, (state: ContentState, action: Action) => handle(state, action, {
		start: (state) => ({
			...state,
			loadingEvents: true,
			eventsError: null,
		}),
		finish: (state) => ({ ...state, loadingEvents: false }),
		failure: (state) => ({ ...state, eventsError: action.payload }),
		success: (state) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			const newState = { ...state };
			const newEvents = JSON.parse(sanitizedPayload);
			if (Array.isArray(newEvents)) newState.events = newEvents;
			return newState;
		},
	})],
	[LOAD_MEETINGS_EVENTS_LEGACY_PRODUCER, (state: ContentState, action: Action) => handle(state, action, {
		start: (state) => ({
			...state,
			loadingEvents: true,
			eventsError: null,
		}),
		finish: (state) => ({ ...state, loadingEvents: false }),
		failure: (state) => ({ ...state, eventsError: action.payload }),
		success: (state) => {
			const newState = { ...state };
			newState.events = action.payload;
			return newState;
		},
	})],
	[LOAD_MEETINGS_SHOWROOMS_PRODUCER, (state: ContentState, action: Action) => handle(state, action, {
		start: (state) => ({
			...state,
			loadingEvents: true,
			eventsError: null,
		}),
		finish: (state) => ({ ...state, loadingEvents: false }),
		failure: (state) => ({ ...state, eventsError: action.payload }),
		success: (state) => {
			const newState = { ...state };
			newState.events = action.payload;
			return newState;
		},
	})],
	[LOAD_MEETINGS_STANDALONE_GREENROOM_PRODUCER, (state: ContentState, action: Action) => handle(state, action, {
		start: (state) => ({
			...state,
			loadingEvents: true,
			eventsError: null,
		}),
		finish: (state) => ({ ...state, loadingEvents: false }),
		failure: (state) => ({ ...state, eventsError: action.payload }),
		success: (state) => {
			const newState = { ...state };
			newState.events = action.payload;
			return newState;
		},
	})],
	[LOAD_MEETINGS_PRESENTER, (state: ContentState, action: Action) => handle(state, action, {
		start: (state) => ({
			...state,
			loadingEvents: true,
			eventsError: null,
		}),
		finish: (state) => ({ ...state, loadingEvents: false }),
		failure: (state) => ({ ...state, eventsError: action.payload }),
		success: (state) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
			const newState = { ...state };
			newState.events = JSON.parse(sanitizedPayload);
			return newState;
		},
	})],
	[SET_LIVE_CHANNELS, (state: ContentState, action: Action) => (
		{ ...state, liveChannels: action.payload }
	)],
	[UPDATE_EVENTS_PRESENTER, (state: ContentState, action: Action) => (
		{ ...state, events: action.payload }
	)],
	[GET_DEFAULT_BACKGROUNDS, (state: ContentState, action: Action) => (
		{ ...state, defaultBackgrounds: action.payload }
	)],
	[LOAD_VBGS, (state: ContentState, action: Action): ContentState => handle(state, action, {
		start: (s) => ({
			...s,
			selectedVBGUrl: action.meta.initialPayload ?? s.selectedVBGUrl,
		}),
		failure: (s) => ({ ...s, loadingVbgs: false }),
		success: (s) => {
			let { selectedVBG } = s;
			const { loadingVbgs, defaultVBGS } = s;
			const virtualBackgrounds = action.payload.reduce((acc: VirtualBg[], vbg: UserMedia) => {
				const bg: VirtualBg = {
					url: vbg.url,
					blur: 0,
					id: vbg.id,
					uniqueId: `uploaded_${vbg.id}`,
				};

				// always choose the latest even if duplicate
				if (vbg.url === s.selectedVBGUrl) {
					selectedVBG = bg;
					localStorage.setItem('stored_vbg', bg.uniqueId);
				}

				acc.push(bg);
				return acc;
			}, []);
			const stored = localStorage.getItem('stored_vbg');
			if (stored && loadingVbgs) {
				if (stored.startsWith('uploaded')) {
					selectedVBG = virtualBackgrounds.find((bg: VirtualBg) => bg.uniqueId === stored) ?? null;
				}
				if (stored.startsWith('default')) {
					selectedVBG = defaultVBGS.find((bg: VirtualBg) => bg.uniqueId === stored) ?? null;
				}
				if (stored.startsWith('blur')) {
					selectedVBG = { blur: stored.split('_')[1] as Blur, url: '', uniqueId: stored };
				}
			}
			return ({
				...s,
				loadingVbgs: false,
				virtualBackgrounds,
				selectedVBG,
				recentVbgUpload: s.uploadingVbgs.size > 0,
			});
		},
	})],
	[SELECT_VBG, (state: ContentState, action: Action) => {
		localStorage.setItem('stored_vbg', action.payload?.uniqueId || '');
		return {
			...state,
			selectedVBG: action.payload,
		};
	}],
	[ADD_UPLOADING_VBG, (state: ContentState, action: Action) => {
		const uploadingVbgs = new Map(state.uploadingVbgs);
		uploadingVbgs.set(action.payload, true);
		return { ...state, recentVbgUpload: true, uploadingVbgs };
	}],
	[REMOVE_UPLOADING_VBG, (state: ContentState, action: Action) => {
		const uploadingVbgs = new Map(state.uploadingVbgs);
		uploadingVbgs.delete(action.payload);
		return { ...state, uploadingVbgs };
	}],

]);

export function ContentReducer(state: ContentState = initialState, action: Action): ContentState {
	return actions.get(action.type)?.(state, action) || state;
}
